import {
  createContext,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from "react";
import { ISetBoolean } from "../../interfaces/AppGlobal/ISetBoolean";
import { IAppGlobalAction } from "../../interfaces/AppGlobal/IAppGlobalAction";
import { IAppGlobalDispatch } from "../../interfaces/AppGlobal/IAppGlobalDispatch";
import { IStorage } from "../../interfaces/Storage/IStorage";
import Axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import {
  LOGIN_FAILURE,
  LOGIN_SUCCESS,
  LOGOUT_SUCCESS,
  SET_SITES,
  RESTORE_USER_FROM_SESSION_SUCCESS,
  RESET_TO_INITIAL_STATE,
  SET_HOME_SELECTION_MODE,
  SET_DEFAULT_PAGE_REQUIRED,
  SET_HEADER_TITLE,
  RESET_SITE_DATA,
  RESET_APP_DATA,
} from "../../actions/actionTypes";

import { signOutUser } from "../../libs/authLib";
import {
  englishUsLocale,
  LANGUAGE_REFRESH_MINUTES,
  PROFILE_REFRESH_MINUTES,
  supportedLocals,
} from "../../constants/global";
import {
  rootReducer,
  IRootState,
} from "../../reducers/rootReducer/rootReducer";
import { ISiteState } from "../../reducers/siteReducer/siteReducer";
import { IUserState } from "../../reducers/userReducer/userReducer";
import { IConfigState } from "../../reducers/configReducer/configReducer";
import { IAppGlobalState } from "../../interfaces/AppGlobal/IAppGlobalState";
import { ICompanyState } from "../../reducers/companyReducer/companyReducer";
import Utils, { getLanguageCodeStr, getLanguageLongStr } from "../../utilities/utils";
import { useLocalStorage } from "../LocalStorageContext/LocalStorageContext";
import { useConfigActions } from "../../actions/configActions";
import { useVrsUserActions } from "../../actions/vrsUserActions";
import { IModelState } from "../../reducers/modelReducer/modelReducer";
import { ISalesforceState } from "../../reducers/salesforceReducer/salesforceReducer";
import { compressLZW, decompressLZW } from "../../utilities/stringutils";
import translations from "../../utilities/translations";
import { refreshDesignAccessToken } from "../../api/Design/DesignActions";

const initialRootState = rootReducer(
  {},
  { type: RESET_TO_INITIAL_STATE, payload: null }
);

export const AppGlobalStateContext =
  createContext<IRootState>(initialRootState);
const AppGlobalDispatchContext = createContext((_: IAppGlobalAction) => { });

function AppGlobalProvider({ children }: { children: ReactElement }) {
  const [state, dispatch] = useReducer(rootReducer, initialRootState);
  const stateRef = useRef(state);
  const dispatchRef = useRef(dispatch);

  // Update refs when state/dispatch changes
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    dispatchRef.current = dispatch;
  }, [dispatch]);

  // Register interceptors only once
  useEffect(() => {
    const requestInterceptor = Axios.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        const currentState = stateRef.current;
        const currentDispatch = dispatchRef.current;

        if (currentState.userState.isAuthenticated && config.headers?.designApi) {
          const accessToken = currentState.userState.designToken;
          const selectedSiteId = currentState.siteState.selectedSiteId;
          const activeSiteId = selectedSiteId ? selectedSiteId : currentState.companyState.selectedSiteIdForCompany === "0" ? "" : currentState.companyState.selectedSiteIdForCompany;
          const { userProfileLarge } = currentState.userState;
          const isTokenInvalid = !accessToken || Utils.IsTokenExpired(accessToken) || !Utils.DoesTokenBelongToActiveSite(accessToken, activeSiteId);

          if (activeSiteId && isTokenInvalid && userProfileLarge) {
            try {
              const isExternal = currentState.configState.appDataInitialization.firstSiteData.isExternal;
              const newAccessToken = await refreshDesignAccessToken(currentDispatch, activeSiteId, isExternal, userProfileLarge);
              config.headers['Authorization'] = `Bearer ${newAccessToken}`;
            } catch (error) {
              console.error('Error refreshing token before request:', error);
              return Promise.reject(error);
            }
          } else if (accessToken) {
            config.headers['Authorization'] = `Bearer ${accessToken}`;
          }
        }
        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    const responseInterceptor = Axios.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      async (error: AxiosError) => {
        const currentState = stateRef.current;
        const currentDispatch = dispatchRef.current;
        const originalRequest = error.config;

        if (currentState.userState.isAuthenticated) {
          if (error.response && originalRequest && originalRequest.headers?.designApi && error.response.status === 401 && !originalRequest["_retry"]) {
            originalRequest["_retry"] = true;

            try {
              const selectedSiteId = currentState.siteState.selectedSiteId;
              const activeSiteId = selectedSiteId ? selectedSiteId : currentState.companyState.selectedSiteIdForCompany === "0" ? "" : currentState.companyState.selectedSiteIdForCompany;
              const { userProfileLarge } = currentState.userState;
              const isExternal = currentState.configState.appDataInitialization.firstSiteData.isExternal;
              const newAccessToken = await refreshDesignAccessToken(currentDispatch, activeSiteId, isExternal, userProfileLarge);

              originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
              return Axios(originalRequest);
            } catch (refreshError) {
              console.error('Error refreshing token after 401:', refreshError);
              return Promise.reject(refreshError);
            }
          }
          else if (
            error.response &&
            error.response.data &&
            typeof error.response.data === 'object' &&
            error.response.data !== null &&
            'errorId' in error.response.data
          ) {
            const message = error.response.data["errorId"];
            return Promise.reject({ message, status: error.response.status });
          } else if (
            error.response &&
            error.response.data &&
            error.response.data["message"]
          ) {
            return Promise.reject({
              message: error.response.data["message"],
              status: error.response.status,
            });
          } else if (
            error.response &&
            error.response.data &&
            error.response.data["error"]
          ) {
            return Promise.reject({
              message: error.response.data["error"],
              status: error.response.status,
            });
          } else if (
            error.response &&
            error.response.data &&
            typeof error.response.data === "string"
          ) {
            return Promise.reject({
              message: error.response.data,
              status: error.response.status,
            });
          } else {
            return Promise.reject(error);
          }
        }
      }
    );

    // Cleanup function to remove interceptors when component unmounts
    return () => {
      if (requestInterceptor !== undefined) {
        Axios.interceptors.request.eject(requestInterceptor);
      }
      if (responseInterceptor !== undefined) {
        Axios.interceptors.response.eject(responseInterceptor);
      }
    };
  }, []); // Empty dependency array - runs only once

  // Handle sites reset when user is not authenticated
  useEffect(() => {
    if (!state.userState.isAuthenticated) {
      dispatch({ type: SET_SITES, payload: [] });
    }
  }, [state.userState.isAuthenticated]);

  return (
    <AppGlobalStateContext.Provider value={state}>
      <AppGlobalDispatchContext.Provider value={dispatch}>
        {children}
      </AppGlobalDispatchContext.Provider>
    </AppGlobalStateContext.Provider>
  );
}

function useAppGlobalState(): IAppGlobalState {
  try {
    const context: IRootState = useContext(AppGlobalStateContext);
    return {
      ...context.configState,
      ...context.siteState,
      ...context.userState,
    };
  } catch (err) {
    throw new Error(
      "useAppGlobalState must be used within a functional component"
    );
  }
}

function useAppCompanyState(): ICompanyState {
  try {
    const context = useContext(AppGlobalStateContext);
    return context.companyState;
  } catch (err) {
    throw new Error(
      "useAppCompanyState must be used within a functional component"
    );
  }
}

function useAppModelState(): IModelState {
  try {
    const context = useContext(AppGlobalStateContext);
    return context.modelState;
  } catch (err) {
    throw new Error(
      "useAppModelState must be used within a functional component"
    );
  }
}

function useAppSiteState(): ISiteState {
  try {
    const context = useContext(AppGlobalStateContext);
    return context.siteState;
  } catch (err) {
    throw new Error(
      "useAppSiteState must be used within a functional component"
    );
  }
}

function useAppSelectedSite(): any {
  try {
    const context = useContext(AppGlobalStateContext);
    return context.siteState.getSelectedSite();
  } catch (err) {
    throw new Error(
      "useAppSiteState must be used within a functional component"
    );
  }
}

function useAppUserState(): IUserState {
  try {
    const context = useContext(AppGlobalStateContext);
    return context.userState;
  } catch (err) {
    throw new Error(
      "useAppUserState must be used within a functional component"
    );
  }
}

function useAppConfigState(): IConfigState {
  try {
    const context = useContext(AppGlobalStateContext);
    return context.configState;
  } catch (err) {
    throw new Error(
      "useAppConfigState must be used within a functional component"
    );
  }
}

function useAppSalesforceState(): ISalesforceState {
  try {
    const context = useContext(AppGlobalStateContext);
    return context.salesforceState;
  } catch (err) {
    throw new Error(
      "useAppSalesforceState must be used within a functional component"
    );
  }
}

function useAppLineState(): Array<any> {
  try {
    const context = useContext(AppGlobalStateContext);
    return context.lineState;
  } catch (err) {
    throw new Error(
      "useAppLineState must be used within a functional component"
    );
  }
}

function useAppShiftState(): Array<any> {
  try {
    const context = useContext(AppGlobalStateContext);
    return context.shiftState;
  } catch (err) {
    throw new Error(
      "useAppShiftState must be used within a functional component"
    );
  }
}

function useAppProductState(): Array<any> {
  try {
    const context = useContext(AppGlobalStateContext);
    return context.productState;
  } catch (err) {
    throw new Error(
      "useAppProductState must be used within a functional component"
    );
  }
}

function useConfigState(): IConfigState {
  try {
    const context = useContext(AppGlobalStateContext);
    return context.configState;
  } catch (err) {
    throw new Error(
      "useConfigState must be used within a functional component"
    );
  }
}

function useVrsTranslationState(): {
  _T: (identifier: string) => string;
  loadVrsTranslations: (locale: string, forceRefresh: boolean) => Promise<void>;
  loadVrsUserProfile: (forceRefresh: boolean) => Promise<string>;
} {
  const userLocalStorage = useLocalStorage();
  const vrsUserActions = useVrsUserActions();
  const configActions = useConfigActions();
  const transformId = useCallback( (id: string) => {
    // redis will keep the event name without special chars
    // replace(replace(replace(ec.EventCodeName,':',''),' ',''),'_','')) EventCodeName
    return id?.toString()
      .replace(/\s/g, "")
      .replace(/_/g, "")
      .replace(/:/g, "");
  }, []);

  try {
    const context = useContext(AppGlobalStateContext);

    const fetchFunction = useCallback(
      (identifier: string) => {
        let locale = navigator.language.split(/[-_]/)[0];
        if (context.configState.vrsUserLocale) {
          locale = context.configState.vrsUserLocale;
        }
        const language = getLanguageLongStr(locale).toLocaleLowerCase();
        const translations = context.configState.vrsTranslations;
        const smallIdTranslations = context.configState.vrsTranslationsWithSmallIdentifier;
        const transformedKeyMaps = context.configState.vrsTransformedKeyMaps || {};
        // Try to get the translation for the current language via identifier
        if (translations && translations[language]) {
          const value = translations[language][identifier];
          if (value) {
            return value;
          }
          // Event Names as stored and returned don't have spaces or underscores, we need to look up the original identifier

          // check simplified event identifier if not found with the original identifier
          const eventIdentifier = transformId(identifier);
          if (transformedKeyMaps[language] && eventIdentifier) {
            const originalKey = transformedKeyMaps[language][eventIdentifier];
            if (originalKey && translations[language][originalKey]) {
              return translations[language][originalKey];
            }
          }
          // check small identifier translations
          if (smallIdTranslations && smallIdTranslations[language]) {
            const smallIdentifierValue = smallIdTranslations[language][identifier ? identifier.toLowerCase() : identifier];
            if (smallIdentifierValue) {
              return smallIdentifierValue;
            }
          }
          // fallback to English if not found and not in production
          if (translations["en-us"] && Utils.isProductionLike()) {
            const engValue = translations["en-us"][identifier];
            if (engValue) {
              return engValue;
            }
          }
        }
        // If not found, return the identifier itself for non-production environments
        if (!Utils.isProductionLike() && identifier && language !== "en-us") {
          return `_T(${identifier})`;
        }
        return identifier;
      },
      [
        context.configState.vrsTranslations,
        context.configState.vrsTranslationsWithSmallIdentifier,
        context.configState.vrsUserLocale,
        transformId,
      ]
    );

    const loadVrsTranslations = useCallback(
      async (locale: string, forceRefresh: boolean) => {
        const language = getLanguageLongStr(locale);
        const culture = getLanguageCodeStr(locale);
        const languageItemsTime = userLocalStorage.getItem("languageItemsTime");
        const needsRefresh = !languageItemsTime ||
          (new Date().getTime() - new Date(languageItemsTime).getTime() > LANGUAGE_REFRESH_MINUTES * 60000);

        // Helper function to create transformed key mapping
        const createTransformedKeyMap = (items: Array<Record<string, string>>) => {
          const transformedMap: Record<string, string> = {};
          if (items && items.length) {
            // Iterate through each record in the array
            items.forEach(item => {
              transformedMap[transformId(item.i)] = item.i;
            });
          }

          return transformedMap;
        };

        // Helper function to fetch and process language items
        const fetchLanguageItems = async (lang: string, cultureCode: string) => {
          configActions.setIsLoading(true);
          try {
            const items = await vrsUserActions.getLanguageItemsAppSync(lang, cultureCode);
            const transformedKeyMap = createTransformedKeyMap(items);

            // Store both items and the transformed key map
            const itemsWithMap = {
              translations: items,
              transformedKeyMap: transformedKeyMap
            };

            const itemsStr = compressLZW(JSON.stringify(itemsWithMap));
            const storageKey = `languageItems-${lang.toLowerCase()}`;
            userLocalStorage.setItem(storageKey, itemsStr);

            // Set both regular translations and the key mapping
            configActions.setLanguageItems(lang.toLowerCase(), items);
            configActions.setTransformedKeyMap(lang.toLowerCase(), transformedKeyMap);

            return items;
          } finally {
            configActions.setIsLoading(false);
          }
        };

        // Process requested language if not English
        if (language !== "en-US") {
          const langKey = `languageItems-${language.toLowerCase()}`;
          let storedItems = userLocalStorage.getItem(langKey);

          // Clear invalid data format
          if (storedItems && storedItems[0] === "[") {
            userLocalStorage.removeItem(langKey);
            storedItems = "";
          }

          if (forceRefresh || !storedItems || needsRefresh) {
            userLocalStorage.setItem("languageItemsTime", new Date().toISOString());
            await fetchLanguageItems(language, culture);

            // Remove other languages to save storage space
            supportedLocals.forEach(loc => {
              if (loc !== language.toLowerCase() && loc !== englishUsLocale) {
                userLocalStorage.removeItem(`languageItems-${loc}`);
              }
            });
          } else {
            // Parse stored items which now includes both translations and transformed key map
            const parsedData = JSON.parse(decompressLZW(storedItems));
            if (parsedData.translations && parsedData.transformedKeyMap) {
              configActions.setLanguageItems(language.toLowerCase(), parsedData.translations);
              configActions.setTransformedKeyMap(language.toLowerCase(), parsedData.transformedKeyMap);
            } else {
              // Handle legacy format (backwards compatibility)
              configActions.setLanguageItems(language.toLowerCase(), parsedData);
              configActions.setTransformedKeyMap(language.toLowerCase(), createTransformedKeyMap(parsedData));
            }
          }
        }

        // Always process English as fallback
        const enKey = "languageItems-en-us";
        let enStoredItems = userLocalStorage.getItem(enKey);

        if (enStoredItems && enStoredItems[0] === "[") {
          userLocalStorage.removeItem(enKey);
          enStoredItems = "";
        }

        if (forceRefresh || !enStoredItems || needsRefresh) {
          userLocalStorage.setItem("languageItemsTime", new Date().toISOString());
          await fetchLanguageItems("en-US", "en_US");
        } else {
          // Parse stored English items
          const parsedData = JSON.parse(decompressLZW(enStoredItems));
          if (parsedData.translations && parsedData.transformedKeyMap) {
            configActions.setLanguageItems("en-us", parsedData.translations);
            configActions.setTransformedKeyMap("en-us", parsedData.transformedKeyMap);
          } else {
            // Handle legacy format
            configActions.setLanguageItems("en-us", parsedData);
            configActions.setTransformedKeyMap("en-us", createTransformedKeyMap(parsedData));
          }
        }
      },
      [configActions, userLocalStorage, vrsUserActions, transformId]
    );

    const loadVrsUserProfile = useCallback(
      async (forceRefresh: boolean) => {
        // Get Vrs language items
        let vrsUserProfileTime = userLocalStorage.getItem(
          "vrsUserProfileLocaleTime"
        );
        const emptyTime = !vrsUserProfileTime;
        if (emptyTime) {
          vrsUserProfileTime = new Date().toISOString();
        }

        const refreshVrsUserProfile =
          new Date().getTime() - new Date(vrsUserProfileTime).getTime();

        const storageValue = userLocalStorage.getItem("vrsUserLocale");
        let vrsUserLocalStr =
          storageValue && storageValue !== "null" ? storageValue : "";
        if (
          forceRefresh ||
          !vrsUserLocalStr ||
          emptyTime ||
          refreshVrsUserProfile > PROFILE_REFRESH_MINUTES * 60000
        ) {
          userLocalStorage.setItem(
            "vrsUserProfileLocaleTime",
            new Date().toISOString()
          );
          try {
            configActions.setIsLoading(true);
            const profiles = await vrsUserActions.getUserProfileAppSync(false);

            const userProfile = profiles.length > 0 ? profiles[0] : null;
            configActions.setPrefer24HourTimeFormat(
              userProfile?.Prefer24HourTimeFormat || false
            );
            if (
              userProfile &&
              userProfile.Languages &&
              (userProfile.LanguageId || userProfile.LanguageId === 0)
            ) {
              const languageProfileItem = userProfile.Languages.find(
                (el) => el.LanguageId === userProfile.LanguageId
              );
              const languageTag = languageProfileItem.IetflanguageTag;
              if (languageProfileItem) {
                if (languageProfileItem.LanguageId === 0) {
                  vrsUserLocalStr = navigator.language.split(/[-_]/)[0] || 'en';
                }
                else if (languageTag && languageTag === "en-US") {
                  vrsUserLocalStr = "en";
                } else {
                  vrsUserLocalStr =
                    languageProfileItem.IetflanguageTag.toLowerCase();
                }
              }
            }

            userLocalStorage.setItem("vrsUserLocale", vrsUserLocalStr);
            configActions.setVrsUserLocale(vrsUserLocalStr);
          } finally {
            configActions.setIsLoading(false);
          }
        } else {
          configActions.setVrsUserLocale(vrsUserLocalStr ? vrsUserLocalStr : "en");
        }

        return vrsUserLocalStr;
      },
      [configActions, userLocalStorage, vrsUserActions]
    );

    return {
      _T: fetchFunction,
      loadVrsTranslations,
      loadVrsUserProfile,
    };
  } catch (err) {
    throw new Error(
      "useVrsTranslationState must be used within a functional component"
    );
  }
}

function useLocalTranslationState(): {
  _translate: (identifier: string) => string;
} {

  try {

    const fetchFunction = useCallback(
      (identifier: string) => {
        const fallbackLocale = 'en';
        const browserLocale = navigator.language?.toLowerCase() ?? fallbackLocale;
        let locale = Object.prototype.hasOwnProperty.call(translations, browserLocale) ? browserLocale : fallbackLocale;
        locale = locale.split(/[-_]/)[0];
        let value = translations[locale][identifier];

        if (!value) {
          value = Utils.isProductionLike()
            ? translations[fallbackLocale][identifier] ?? identifier
            : `_translate(${identifier})`;
        }

        return value;
      },
      []
    );

    return {
      _translate: fetchFunction,
    };
  } catch (err) {
    throw new Error(
      "useLocalTranslationState must be used within a functional component"
    );
  }
}

function useConfigCacheState(): any {
  try {
    const context = useContext(AppGlobalStateContext);
    return useCallback(
      (name, key) =>
        (context.configState.appCache[name] || []).find((el) => el.key === key)
          ?.value,
      [context.configState.appCache]
    );
  } catch (err) {
    throw new Error(
      "useConfigState must be used within a functional component"
    );
  }
}

function useIsLoading(): boolean {
  try {
    const context = useContext(AppGlobalStateContext);
    return context.configState.isLoading;
  } catch (err) {
    throw new Error(
      "useConfigState must be used within a functional component"
    );
  }
}

function useAppGlobalDispatch(): IAppGlobalDispatch {
  try {
    const context = useContext(AppGlobalDispatchContext);
    return context;
  } catch (err) {
    throw new Error(
      "useAppGlobalDispatch must be used within a functional component"
    );
  }
}

function useSelectedSite() {
  try {
    const context = useContext(AppGlobalStateContext);
    const sites = context.siteState.sites;
    if (sites.length === 0) {
      return null;
    }

    const selectedSites = sites.filter(
      (s) => s.id === context.siteState.selectedSiteId
    );
    if (selectedSites) {
      return selectedSites[0];
    }

    const selectedSitesBySelection = sites.filter((s) => s.selected === true);
    if (selectedSitesBySelection) {
      return selectedSitesBySelection[0];
    }
    return null;
  } catch (err) {
    throw new Error(
      "useAppGlobalDispatch must be used within a functional component"
    );
  }
}

async function loginUser(
  dispatch: IAppGlobalDispatch,
  userSessionStorage: IStorage,
  userProfile: any,
  setIsLoading: ISetBoolean,
  setError: ISetBoolean
) {
  dispatch({ type: LOGIN_SUCCESS, payload: userProfile });
  setError(false);
  setIsLoading(true);

  if (userProfile) {
    userSessionStorage.setItem("cognito_user", JSON.stringify(userProfile));
    setError(false);
    setIsLoading(false);
  } else {
    userSessionStorage.removeItem("cognito_user");
    dispatch({ type: LOGIN_FAILURE, payload: null });
    setError(true);
    setIsLoading(false);
  }
}

async function restoreUserFromSession(
  dispatch: IAppGlobalDispatch,
  userProfile: any
) {
  dispatch({ type: RESTORE_USER_FROM_SESSION_SUCCESS, payload: userProfile });
}

function useLogOut() {
  const selectedSiteId = useSelectedSite();
  const userLocalStorage = useLocalStorage();
  return async function (dispatch: IAppGlobalDispatch) {
    const redirectURL = `${window.location.origin}/login`;
    await signOutUser();
    dispatch({ type: RESET_TO_INITIAL_STATE, payload: true });
    dispatch({ type: SET_DEFAULT_PAGE_REQUIRED, payload: true });
    dispatch({ type: SET_HOME_SELECTION_MODE, payload: 0 });
    dispatch({ type: LOGOUT_SUCCESS, payload: null });
    dispatch({
      type: SET_HEADER_TITLE,
      payload: { title: "", icon: null, linkParams: { ignoreItSelf: true } },
    });
    dispatch({ type: RESET_SITE_DATA, payload: { id: selectedSiteId } });
    dispatch({ type: RESET_APP_DATA, payload: true });

    window.location.assign(redirectURL);

    userLocalStorage.removeItem("vrsUserProfileLocaleTime");
    userLocalStorage.removeItem("languageItemsTime");
  };
}

export {
  AppGlobalProvider,
  useAppGlobalState,
  useAppSiteState,
  useAppCompanyState,
  useAppConfigState,
  useConfigCacheState,
  useAppUserState,
  useAppLineState,
  useAppShiftState,
  useAppProductState,
  useConfigState,
  useAppGlobalDispatch,
  loginUser,
  useLogOut,
  restoreUserFromSession,
  useSelectedSite,
  useAppSelectedSite,
  useIsLoading,
  useVrsTranslationState,
  useLocalTranslationState,
  useAppModelState,
  useAppSalesforceState,
};

